<script setup lang="ts">
import { computed, inject } from "vue";

import type { ThemeContext } from "@core/composables/ui/useTheme";

interface Props {
  compact?: boolean;
}

withDefaults(defineProps<Props>(), {
  compact: false
});

const theme = inject<ThemeContext>("theme");
const isDark = computed(() => theme?.isDark?.value ?? false);
</script>

<template>
  <div>
    <!-- Compact mode - icon only -->
    <template v-if="compact">
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="39" viewBox="140 0 42 39">
        <g data-name="Group 376">
          <path
            fill="#23e25b"
            d="m151.293 37.673 18.031-36.067c.973-2.017 14.122-2.2 13.149-.183l-16.756 35.841c-.972 2.016-15.4 2.425-14.424.409"
            data-name="Path 8"
          />
          <path
            fill="#246531"
            d="m157.672 9.712-4.053-8.106c-.972-2.016-14.121-2.198-13.149-.182l9.977 21.339c.972 2.017 8.2-11.035 7.225-13.051"
            data-name="Path 9"
          />
        </g>
      </svg>
    </template>
    <!-- Light mode logo -->
    <svg
      v-else-if="!isDark"
      xmlns="http://www.w3.org/2000/svg"
      width="182.525"
      height="38.996"
      viewBox="0 0 182.525 38.996"
    >
      <g data-name="Group 418">
        <g fill="#0b1f06" data-name="Group 375">
          <path
            d="M8.321 32.17v-9.538L.001 7.873h5.152l5.606 10.269h.068l5.464-10.269h5.014L12.95 22.597v9.572Z"
            data-name="Path 1"
          />
          <path
            d="M21.04 23.047c0-5.814 3.238-9.466 8.425-9.466 5.222 0 8.458 3.652 8.458 9.466 0 5.851-3.236 9.539-8.458 9.539-5.187 0-8.425-3.689-8.425-9.539m12.22 1.707v-3.375c0-2.715-1.463-4.213-3.8-4.213-2.3 0-3.759 1.5-3.759 4.213v3.375c0 2.75 1.459 4.246 3.759 4.246 2.332 0 3.8-1.5 3.8-4.246"
            data-name="Path 2"
          />
          <path
            d="M52.729 29.143h-.176a4.93 4.93 0 0 1-5.082 3.443c-3.724 0-5.814-2.572-5.814-7.1V13.999h4.456v11.034c0 2.508.941 3.867 3.064 3.867 1.775 0 3.551-.941 3.551-2.891v-12.01h4.455V32.17h-4.454Z"
            data-name="Path 3"
          />
          <path
            d="M62.624 32.168V7.871h10.929c4.494 0 7.209 3.027 7.209 7.517 0 4.527-2.715 7.519-7.209 7.519h-6.334v9.261Zm4.595-13.228h5.918a2.558 2.558 0 0 0 2.855-2.75v-1.599a2.532 2.532 0 0 0-2.855-2.715h-5.918Z"
            data-name="Path 4"
          />
          <path
            d="M82.895 27.227c0-3.726 2.82-5.57 7.763-5.57h3.1v-1.323c0-2.019-1.008-3.2-3.375-3.2a4.73 4.73 0 0 0-4.145 2.3l-2.644-2.367c1.324-2.09 3.447-3.482 7.1-3.482 4.908 0 7.519 2.334 7.519 6.474v8.564h1.811v3.551h-2.472a3.3 3.3 0 0 1-3.375-3.132h-.211c-.592 2.332-2.576 3.548-5.291 3.548-3.691 0-5.779-2.123-5.779-5.359m10.863-.491v-2.4h-2.857c-2.332 0-3.48.8-3.48 2.228v.589c0 1.463 1.008 2.195 2.748 2.195 1.984 0 3.586-.9 3.586-2.611"
            data-name="Path 5"
          />
          <path
            d="M103.654 32.17V13.999h4.455v3.76h.174a4.66 4.66 0 0 1 4.8-3.76h.975v4.211h-1.392c-3 0-4.562.836-4.562 2.82v11.14Z"
            data-name="Path 6"
          />
          <path
            d="M117.342 6.409h4.455v15.525h.174l2.4-3.273 4.072-4.664h5.046l-6.261 7.029 6.929 11.14h-5.291l-4.667-8.14-2.4 2.611v5.535h-4.455Z"
            data-name="Path 7"
          />
        </g>
        <g data-name="Group 376">
          <path
            fill="#23e25b"
            d="m151.293 37.673 18.031-36.067c.973-2.017 14.122-2.2 13.149-.183l-16.756 35.841c-.972 2.016-15.4 2.425-14.424.409"
            data-name="Path 8"
          />
          <path
            fill="#246531"
            d="m157.672 9.712-4.053-8.106c-.972-2.016-14.121-2.198-13.149-.182l9.977 21.339c.972 2.017 8.2-11.035 7.225-13.051"
            data-name="Path 9"
          />
        </g>
      </g>
    </svg>

    <!-- Dark mode logo -->
    <svg v-else xmlns="http://www.w3.org/2000/svg" width="182.525" height="38.996" viewBox="0 0 182.525 38.996">
      <g data-name="Group 418">
        <g fill="#ffffff" data-name="Group 375">
          <path
            d="M8.321 32.17v-9.538L.001 7.873h5.152l5.606 10.269h.068l5.464-10.269h5.014L12.95 22.597v9.572Z"
            data-name="Path 1"
          />
          <path
            d="M21.04 23.047c0-5.814 3.238-9.466 8.425-9.466 5.222 0 8.458 3.652 8.458 9.466 0 5.851-3.236 9.539-8.458 9.539-5.187 0-8.425-3.689-8.425-9.539m12.22 1.707v-3.375c0-2.715-1.463-4.213-3.8-4.213-2.3 0-3.759 1.5-3.759 4.213v3.375c0 2.75 1.459 4.246 3.759 4.246 2.332 0 3.8-1.5 3.8-4.246"
            data-name="Path 2"
          />
          <path
            d="M52.729 29.143h-.176a4.93 4.93 0 0 1-5.082 3.443c-3.724 0-5.814-2.572-5.814-7.1V13.999h4.456v11.034c0 2.508.941 3.867 3.064 3.867 1.775 0 3.551-.941 3.551-2.891v-12.01h4.455V32.17h-4.454Z"
            data-name="Path 3"
          />
          <path
            d="M62.624 32.168V7.871h10.929c4.494 0 7.209 3.027 7.209 7.517 0 4.527-2.715 7.519-7.209 7.519h-6.334v9.261Zm4.595-13.228h5.918a2.558 2.558 0 0 0 2.855-2.75v-1.599a2.532 2.532 0 0 0-2.855-2.715h-5.918Z"
            data-name="Path 4"
          />
          <path
            d="M82.895 27.227c0-3.726 2.82-5.57 7.763-5.57h3.1v-1.323c0-2.019-1.008-3.2-3.375-3.2a4.73 4.73 0 0 0-4.145 2.3l-2.644-2.367c1.324-2.09 3.447-3.482 7.1-3.482 4.908 0 7.519 2.334 7.519 6.474v8.564h1.811v3.551h-2.472a3.3 3.3 0 0 1-3.375-3.132h-.211c-.592 2.332-2.576 3.548-5.291 3.548-3.691 0-5.779-2.123-5.779-5.359m10.863-.491v-2.4h-2.857c-2.332 0-3.48.8-3.48 2.228v.589c0 1.463 1.008 2.195 2.748 2.195 1.984 0 3.586-.9 3.586-2.611"
            data-name="Path 5"
          />
          <path
            d="M103.654 32.17V13.999h4.455v3.76h.174a4.66 4.66 0 0 1 4.8-3.76h.975v4.211h-1.392c-3 0-4.562.836-4.562 2.82v11.14Z"
            data-name="Path 6"
          />
          <path
            d="M117.342 6.409h4.455v15.525h.174l2.4-3.273 4.072-4.664h5.046l-6.261 7.029 6.929 11.14h-5.291l-4.667-8.14-2.4 2.611v5.535h-4.455Z"
            data-name="Path 7"
          />
        </g>
        <g data-name="Group 376">
          <path
            fill="#23e25b"
            d="m151.293 37.673 18.031-36.067c.973-2.017 14.122-2.2 13.149-.183l-16.756 35.841c-.972 2.016-15.4 2.425-14.424.409"
            data-name="Path 8"
          />
          <path
            fill="#246531"
            d="m157.672 9.712-4.053-8.106c-.972-2.016-14.121-2.198-13.149-.182l9.977 21.339c.972 2.017 8.2-11.035 7.225-13.051"
            data-name="Path 9"
          />
        </g>
      </g>
    </svg>
  </div>
</template>
